import React, { useEffect } from 'react'
import { getSession } from '../../../../auth'
import api from '../../../../api'
import dayjs from 'dayjs'
import { formatGroupParticipantsUpdateText } from '../../../../utils/scout'

let socketregistry = 0
const socketinterval = 20000

let latestSocketParentRef = { parent: null, setParent: null }

const refreshGroupDetails = (remoteJid) => {
    const enterpriseId = getSession()?.profile?.enterprise?.id
    const activeAccount = latestSocketParentRef.parent?.activeAccount
    if (!enterpriseId || !activeAccount?.id) return

    api.get(`api/enterprises/${enterpriseId}/scout_accounts/${activeAccount.id}/conversation/${encodeURIComponent(remoteJid)}`)
        .then((response) => {
            const conversations = Array.isArray(response?.data) ? response.data : []
            const match = conversations.find((item) => item?.id === remoteJid)
            if (!match) return

            const parent = latestSocketParentRef.parent
            const setParent = latestSocketParentRef.setParent
            if (!parent || !setParent) return

            const chat = parent.chats.find((item) => item.id === remoteJid)
            if (!chat) return

            setParent({
                chats: parent.chats.map((item) => item.id === remoteJid ? {
                    ...item,
                    name: match.name || item.name,
                    profile_picture: match.profile_picture || item.profile_picture,
                } : item)
            })
        })
        .catch(() => {})
}

const eventAliases = {
    'contacts.upsert': 'contacts.update',
    'chats.upsert': 'chats.update',
    'messages.update': 'messages.upsert',
}

const getEventName = (data) => eventAliases[data?.event] || data?.event

const getMessage = (data) => {
    const rawMessage = data?.message || data?.data || {}
    const message = Array.isArray(rawMessage) ? rawMessage[0] : rawMessage
    if (!message || typeof message !== 'object') return {}
    return {
        ...message,
        instanceId: message.instanceId || data?.instanceId || data?.instance,
        instance: message.instance || data?.instance
    }
}

const getInstanceId = (message) => message?.instanceId || message?.instance || message?.owner || message?.data?.instance || message?.data?.instanceId

const getActiveAccountInstance = (account) => account?.owner || account?.instance_name || account?.instance

const getActiveAccountInstances = (account) => [
    account?.owner,
    account?.instance_name,
    account?.instance
].filter(Boolean)

const isActiveAccountEvent = (activeAccount, message) => {
    if (!activeAccount) return false
    const instanceId = getInstanceId(message)
    return !instanceId || getActiveAccountInstances(activeAccount).includes(instanceId)
}

const getRemoteJid = (message) => {
    return message?.source ||
        message?.remoteJid ||
        message?.remotejid ||
        message?.remote_jid ||
        message?.key?.remoteJid ||
        message?.message?.reactionMessage?.key?.remoteJid ||
        message?.id
}

const getMessageType = (message) => message?.messageType || message?.message_type || message?.messagetype

const mediaMessageTypes = [
    'imageMessage',
    'audioMessage',
    'videoMessage',
    'stickerMessage',
    'documentMessage',
    'documentWithCaptionMessage',
]

const normalizeArrayInput = (value) => {
    if (Array.isArray(value)) return value
    if (value === null || value === undefined) return []
    return [value]
}

const normalizeThumbnail = (thumbnail) => {
    if (thumbnail && typeof thumbnail === 'object')
        return thumbnail?.$binary?.base64 || thumbnail?.base64 || ''
    return thumbnail
}

const getFileSize = (message) => {
    if (typeof message?.fileLength === 'object') return message.fileLength.low || 0
    return message?.fileLength || message?.size || 0
}

const normalizeMessage = (message) => {
    const key = message?.key || {}
    const messageType = getMessageType(message)
    const payload = message?.message && typeof message.message === 'object' ? message.message : {}
    const textMessage = typeof message?.message === 'string' ? message.message : ''
    const fromMe = message?.fromMe !== undefined ? message.fromMe : key.fromMe
    const normalized = {
        ...message,
        timestamp: message?.timestamp || message?.messageTimestamp || dayjs().unix(),
        fromMe: fromMe,
        messageType: messageType,
        source: getRemoteJid(message),
        pushName: message?.pushName || message?.sender || '',
        id: key.id || message?.id || message?.messageId || message?.chatMessageId,
        key: key
    }

    if (messageType === 'conversation') {
        normalized.message = textMessage || payload?.conversation || message?.conversation || ''
    } else if (messageType === 'extendedTextMessage') {
        const extendedTextMessage = payload?.extendedTextMessage || message?.extendedTextMessage || {}
        normalized.message = textMessage || extendedTextMessage.text || ''
        normalized.extendedTextMessage = extendedTextMessage
    } else if (mediaMessageTypes.includes(messageType)) {
        const media = payload?.[messageType] || message?.[messageType] || {}
        normalized.caption = message?.caption || media?.caption
        normalized.base64 = message?.base64
        normalized.mediaUrl = message?.mediaUrl || payload?.mediaUrl || media?.mediaUrl || ''
        normalized.mimetype = message?.mimetype || media?.mimetype
        normalized.title = message?.title || media?.fileName || media?.title
        normalized.size = getFileSize(media) || message?.size
        normalized.thumbnail = message?.thumbnail || normalizeThumbnail(media?.jpegThumbnail)
    } else if (messageType === 'templateMessage') {
        normalized.templateMessage = message?.templateMessage || payload?.templateMessage
    } else if (messageType === 'reactionMessage') {
        const reaction = payload?.reactionMessage || {}
        normalized.message = payload?.reaction ?
            payload :
            {
                reaction: reaction.text,
                fromMe: fromMe,
                contact: fromMe ? message?.owner : key.participant || key.remoteJid,
                reactedMessageId: reaction?.key?.id,
                conversation: key.remoteJid,
            }
    } else if (['protocolMessage', 'REVOKE', 'editedMessage'].includes(messageType)) {
        const protocolMessage = payload?.protocolMessage || message?.protocolMessage
        normalized.message = textMessage || message?.conversation || protocolMessage?.editedMessage?.conversation || ''
        normalized.protocolMessage = protocolMessage
    }

    return normalized
}

const normalizeRealtimeGroupParticipantsUpdate = (message) => {
    const remoteJid = getRemoteJid(message)
    const participants = normalizeArrayInput(message?.participants || message?.participant || message?.members || message?.member)
    const participantsData = normalizeArrayInput(message?.participantsData || message?.participants_data || message?.participantData || message?.participant_data)
        .filter((participant) => participant && typeof participant === 'object')
    const timestamp = message?.timestamp || message?.messageTimestamp || dayjs().unix()
    const participantsKey = (participantsData.length ? participantsData : participants)
        .map((participant) => typeof participant === 'object'
            ? participant?.participant_jid || participant?.participant_lid || participant?.jid || participant?.id || participant?.phoneNumber || participant?.phone_number || participant?.participant_phone
            : participant
        )
        .filter(Boolean)
        .join(',')

    const normalized = {
        ...message,
        timestamp: timestamp,
        fromMe: false,
        messageType: 'groupParticipantsUpdate',
        simplified_type: 'group_participants_update',
        source: remoteJid,
        pushName: message?.pushName || message?.push_name || message?.sender || '',
        id: message?.key?.id || message?.id_message || `group-participants.update:${remoteJid}:${message?.action || ''}:${message?.author || message?.authorPn || ''}:${participantsKey}:${timestamp}`,
        message: {
            ...message,
            participants: participants,
            participantsData: participantsData,
        }
    }

    normalized.messageText = formatGroupParticipantsUpdateText(normalized)
    return normalized
}

const buildChat = (remoteJid, activeAccount, message) => {
    const [phone_num, type] = remoteJid.split('@')
    const instanceId = getInstanceId(message) || getActiveAccountInstance(activeAccount)

    return {
        id: remoteJid,
        last_message_timestamp : dayjs().unix(),
        account_id: activeAccount.id,
        instance_name: instanceId,
        owner: instanceId,
        phone_num: phone_num,
        type: type === 'g.us' ? 'GROUP' : 'CONTACT',
        message: {}
    }
}

const updateChatWithMessage = (chat, message) => ({
    ...chat,
    name: chat.kwik_contact_name || (chat.type === 'CONTACT' && message.fromMe === false ? message.pushName : '') || chat.name,
    last_message_timestamp: message.timestamp || message.messageTimestamp || dayjs().unix(),
    message: message,
    message_type: getMessageType(message),
    fromMe: message.fromMe,
    sender: message.pushName || message.sender || chat.sender
})

const parseSocketData = (data) => {
    try {
        return JSON.parse(data)
    } catch (error) {
        console.error('Invalid scout websocket message received.', error)
        return null
    }
}

const getSocketChannel = () => getSession().profile.enterprise.accountcode

export const treatEvents = (data, state, setState, windowRef) => {
    switch (getEventName(data)){
        case 'PONG':
        break
        case 'chats.update': {
            /* Any chat updated, you need to select an account to track this chats... If new chat is received, it should be appended, otherwise it's ignored*/
            const message = getMessage(data)
            if (!isActiveAccountEvent(state.activeAccount, message)) return
            const remoteJid = getRemoteJid(message)
            if (!remoteJid) return
            const chat = state.chats.find(chat => chat.id === remoteJid)
            if (!chat){
                const [phone_num, type] = remoteJid.split('@')
                if (phone_num === state.activeAccount.phone_number) return
                setState({chats: [buildChat(remoteJid, state.activeAccount, message), ...state.chats]})
                if (type === 'g.us') refreshGroupDetails(remoteJid)
            }
        break
        }
        case 'messages.upsert': {
            /* New messages received, if you have an openchat, this message will be included on your chat. */
            if (!state.activeAccount) return

            const message = normalizeMessage(getMessage(data))
            if (!isActiveAccountEvent(state.activeAccount, message)) return
            const remoteJid = getRemoteJid(message)
            if (!remoteJid) return

            if (
                ['encEventResponseMessage', 'eventResponseMessage'].includes(message.messageType) ||
                String(message.status || '').toUpperCase() === 'EVENT_RESPONSE'
            ) {
                if (state.activeChat?.id === remoteJid) windowRef.updateEventResponses?.(message)
                return
            }

            let chat = state.chats.find(chat => chat.id === remoteJid)
            if (chat){
                const updatedChat = updateChatWithMessage(chat, message)
                setState({
                    chats: state.chats.map(item => item.id === remoteJid ? updatedChat : item),
                    ...(state.activeChat?.id === remoteJid ? {
                        activeChat: { ...state.activeChat, name: updatedChat.name }
                    } : {})
                })
            } else {
                const [phone_num, type] = remoteJid.split('@')
                if (phone_num !== state.activeAccount.phone_number) {
                    chat = updateChatWithMessage(buildChat(remoteJid, state.activeAccount, message), message)
                    setState({
                        chats: [chat, ...state.chats]
                    })
                    if (type === 'g.us') refreshGroupDetails(remoteJid)
                }
            }
            if (state.activeChat?.id === remoteJid)
                windowRef.addMessages?.(message)
        
        break
        }
        case 'group-participants.update': {
            if (!state.activeAccount) return

            const message = normalizeRealtimeGroupParticipantsUpdate(getMessage(data))
            if (!isActiveAccountEvent(state.activeAccount, message)) return
            const remoteJid = getRemoteJid(message)
            if (!remoteJid) return

            let chat = state.chats.find(chat => chat.id === remoteJid)
            if (chat){
                const updatedChat = updateChatWithMessage(chat, message)
                setState({
                    chats: state.chats.map(item => item.id === remoteJid ? updatedChat : item)
                })
            } else {
                const [phone_num, type] = remoteJid.split('@')
                if (phone_num !== state.activeAccount.phone_number) {
                    chat = updateChatWithMessage(buildChat(remoteJid, state.activeAccount, message), message)
                    setState({
                        chats: [chat, ...state.chats]
                    })
                    if (type === 'g.us') refreshGroupDetails(remoteJid)
                }
            }

            if (state.activeChat?.id === remoteJid)
                windowRef.addMessages?.(message)
        break
        }
        case 'connection.update': {
             /* Connection updated, if an account is online and goes to offline, should close deactivate chat and account, closing the chat */
            const message = getMessage(data)
            const instanceId = getInstanceId(message)
            const account = state.accounts.find(account => account.instance_name === instanceId || account.owner === instanceId)
            const status = message?.state || message?.data?.state
            if (!status) return
            if (account && account.status !== status){
                const newstate = {accounts: state.accounts.map(item => item.id === account.id ? {...item, status: status} : item)}
                if (status !== 'open' && state.activeAccount?.instance_name === account?.instance_name){
                    newstate.activeChat = null
                    newstate.activeAccount = null
                }
                setState(newstate)
            }
        break
        }
        case 'contacts.update': {
            /* Any chat updated, you need to select an account to track this chats... The chat should be on your list, and it must be, then, it will update chat data*/
            const message = getMessage(data)
            if (!isActiveAccountEvent(state.activeAccount, message)) return
            const remoteJid = getRemoteJid(message)
            if (!remoteJid) return
            const chat = state.chats.find(chat => chat.id === remoteJid)
            if (chat && !chat.id.includes('@g.us')){
                const updatedChat = {
                    ...chat,
                    name: message.pushName || chat.name,
                    profile_picture: message.profilePicUrl || chat.profile_picture
                }
                setState({
                    chats: state.chats.map(item => item.id === remoteJid ? updatedChat : item)
                })
            }
            
        break
        }
        case 'groups.upsert':
        case 'groups.update': {
            const message = getMessage(data)
            if (!isActiveAccountEvent(state.activeAccount, message)) return
            const remoteJid = getRemoteJid(message)
            if (!remoteJid) return
            if (!message.subject) {
                refreshGroupDetails(remoteJid)
                return
            }
            const chat = state.chats.find(chat => chat.id === remoteJid)
            if (chat) {
                setState({
                    chats: state.chats.map(item => item.id === remoteJid ? { ...item, name: message.subject } : item)
                })
                if (!chat.profile_picture) refreshGroupDetails(remoteJid)
            } else {
                setState({
                    chats: [{ ...buildChat(remoteJid, state.activeAccount, message), name: message.subject }, ...state.chats]
                })
                refreshGroupDetails(remoteJid)
            }
        break
        }
        case 'qrcode.updated':
        break

        default:
            console.error(`Unknown event '${getEventName(data)}' received from scout.`)
    }
        
}

const connectSocket = (socket, setParent) => {
    if (socket) socket.close()
    const channel = getSocketChannel()
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws'
    const newsocket = new WebSocket(`${protocol}://${location.host}/chat/ws/scout/${channel}/`)
    const sendPing = () => {
        if (newsocket.readyState === WebSocket.OPEN) newsocket.send(JSON.stringify({
            event: 'PING',
            message: ''
        }));
    }

    newsocket.onopen = () => {
        clearInterval(socketregistry)
        sendPing()
        socketregistry = setInterval(sendPing, socketinterval)
    }

    newsocket.onclose = () => {
        clearInterval(socketregistry)
        setParent({socket: null})
    }

    newsocket.onerror = () => {
        newsocket.close()
    }
    
    setParent({socket: newsocket})
}

const ScoutSocketComponent = ({parent, setParent, windowRef}) => {
    latestSocketParentRef = { parent, setParent }

    if (parent.socket) parent.socket.onmessage = (e) => {
        const data = parseSocketData(e.data)
        if (data) treatEvents(data, parent, setParent, windowRef)
    }

    useEffect(()=>{
        if (!parent.socket) connectSocket(parent.socket, setParent)
        return () => {if (parent.socket) parent.socket.close()}
    }, [parent.socket])

    return null
}

export default ScoutSocketComponent
